import { useEffect } from 'react';
import { Subscription, timer } from 'rxjs';
import { useUserProvider } from '../../api/user.provider';
import { runInBackground } from '../../util/async';
import {
  useDirectConnectGetSession,
  useDirectConnectGetSessionLazy,
  useDirectConnectRemoveSession,
} from '../cache/direct-connect-session-cache';

export interface DirectConnectSession {
  sessionId: string;
  expiresAt: string;
}

export type DirectConnectSessionProvider = () => Promise<
  DirectConnectSession | undefined
>;

const useDirectConnectSessionProvider = (): DirectConnectSessionProvider => {
  const activeSession = useDirectConnectGetSession();
  const removeSession = useDirectConnectRemoveSession();
  const { setUserInfo } = useUserProvider();

  useEffect(() => {
    let subscription: Subscription | undefined;

    if (activeSession) {
      const date = new Date(activeSession.expiresAt);

      subscription = timer(date).subscribe(() => {
        runInBackground(async () => {
          // once the session is expired go ahead and remove from cache
          await removeSession();

          // set user info to undefined which will trigger the select user
          // gate to show
          setUserInfo(undefined);
        });
      });
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [activeSession, setUserInfo, removeSession]);

  return useDirectConnectGetSessionLazy();
};

export { useDirectConnectSessionProvider };
